// frontend/src/components/RuralPreferenceTable.js

import React from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import '../styles/PreferenceTable.css';
import { buildCycleKeyLabel, buildPriorityKeyLabel } from '../utils/preferenceUtils';

const DEFAULT_CYCLES = Array.from({ length: 7 }, (_, index) => buildCycleKeyLabel(index + 1));
const DEFAULT_PREFERENCE_ORDER = Array.from({ length: 8 }, (_, index) => buildPriorityKeyLabel(index + 1));

const parsePreferenceOrder = (label) => {
  if (typeof label !== 'string') {
    return undefined;
  }
  const match = label.match(/\d+/);
  if (!match) {
    return undefined;
  }
  const value = Number.parseInt(match[0], 10);
  return Number.isNaN(value) ? undefined : value;
};

const RuralPreferenceTable = ({
  preferences,
  isSidebarOpen,
  onRemove,
  duplicates,
  departmentTermCapacities,
  cycles = DEFAULT_CYCLES,
  preferenceOrder = DEFAULT_PREFERENCE_ORDER,
  cycleSettings = {},
  priorityOptions = [],
  onCyclePriorityChange,
  onCycleExclusionChange,
}) => {
  const getCapacity = (value, cycle) => {
    if (!value || !value.department_term_capacity_id) {
      return null;
    }
    const termIndex = cycles.indexOf(cycle);
    const termId = termIndex === -1 ? null : termIndex + 1;
    if (termId === null) {
      return null;
    }
    const capacityInfo = (departmentTermCapacities || []).find(
      (cap) => cap.id === value.department_term_capacity_id && cap.term_id === termId,
    );
    return capacityInfo ? capacityInfo.capacity : null;
  };

  const renderCycleHeader = (cycle) => {
    const settings = cycleSettings?.[cycle] ?? {
      priority: cycles.indexOf(cycle) + 1,
      excluded: false,
    };
    const headerPriority = settings.priority ?? cycles.indexOf(cycle) + 1;
    return (
      <div className="flex flex-col items-center gap-2 py-2">
        <span className="font-bold text-gray-700">{cycle}</span>
        <div className="flex flex-col items-center gap-2 w-full px-2" data-tour="cycle-settings">
          {onCyclePriorityChange && priorityOptions.length > 0 && (
            <select
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
              value={headerPriority}
              onChange={(event) => onCyclePriorityChange(cycle, event.target.value)}
              disabled={settings.excluded}
              data-tour="priority-select"
            >
              {priorityOptions.map((option) => (
                <option key={option} value={option}>
                  {`第${option}順位`}
                </option>
              ))}
            </select>
          )}
          {onCycleExclusionChange && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer hover:text-gray-900 transition-colors" data-tour="exclude-checkbox">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={settings.excluded}
                onChange={(event) => onCycleExclusionChange(cycle, event.target.checked)}
              />
              <span>希望しない</span>
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`preference-table-container ${isSidebarOpen ? 'sidebar-open' : ''} overflow-x-auto`} data-tour="preference-table">
      <table className="w-full h-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-20 p-2 border-b-2 border-gray-200 bg-gray-50 text-left font-semibold text-gray-600 sticky left-0 z-10"></th>
            {cycles.map((cycle, index) => (
              <th key={cycle} className="min-w-[140px] p-2 border-b-2 border-gray-200 bg-gray-50 text-center font-semibold text-gray-600" data-tour={index === 0 ? 'cycle-header' : undefined}>
                {renderCycleHeader(cycle)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preferenceOrder.map((preferenceKey, rowIndex) => (
            <tr key={preferenceKey} className="hover:bg-gray-50 transition-colors">
              <td className="p-3 border-b border-gray-200 font-medium text-gray-700 bg-white sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                {preferenceKey}
              </td>
              {cycles.map((cycle, colIndex) => {
                const value = preferences?.[preferenceKey]?.[cycle];
                const isDuplicate =
                  value && duplicates?.[cycle]?.[value.department_term_capacity_id];
                const capacity = getCapacity(value, cycle);
                const isInvalidCapacity = capacity !== null && capacity <= 0;
                const isInvalid = value && value.isInvalid;
                const priorityOrderNumber = parsePreferenceOrder(preferenceKey) || 0;
                const settings = cycleSettings?.[cycle];
                const isExcluded = Boolean(settings?.excluded);
                return (
                  <DroppableCell
                    key={`${preferenceKey}-${cycle}`}
                    id={`preference-${preferenceKey}-${cycle}`}
                    value={value}
                    onRemove={() => onRemove(preferenceKey, cycle)}
                    isDuplicate={isDuplicate}
                    isInvalidCapacity={isInvalidCapacity}
                    isInvalid={isInvalid}
                    disabled={isExcluded}
                    data-department-id={value && value.department_term_capacity_id}
                    data-preference-order={priorityOrderNumber}
                    data-tour={rowIndex === 0 && colIndex === 0 ? 'preference-cell' : undefined}
                  >
                    {value && (
                      <DraggableItem
                        id={`preference-${preferenceKey}-${cycle}`}
                        data={value}
                        isDuplicate={isDuplicate}
                        isInvalidCapacity={isInvalidCapacity}
                        isInvalid={isInvalid}
                      >
                        {value.display_name}
                      </DraggableItem>
                    )}
                  </DroppableCell>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DroppableCell = ({
  id,
  children,
  onRemove,
  isDuplicate,
  isInvalidCapacity,
  isInvalid,
  disabled = false,
  ...props
}) => {
  const { setNodeRef, isOver } = useDroppable({ id, disabled });

  if (disabled) {
    return (
      <td className="p-2 border-b border-gray-200 bg-gray-100 text-center align-middle" {...props}>
        <div className="h-full min-h-12 flex items-center justify-center text-gray-400 text-sm italic bg-gray-100 rounded border border-dashed border-gray-300">
          -
        </div>
      </td>
    );
  }

  const handleContextMenu = (event) => {
    event.preventDefault();
    onRemove();
  };

  let cellClass = "h-full flex items-center justify-center rounded border transition-all duration-200 min-h-12 p-1 text-base text-center break-words";
  
  if (isDuplicate) {
    cellClass += " bg-red-50 border-red-300 text-red-700";
  } else if (isInvalidCapacity) {
    cellClass += " bg-orange-50 border-orange-300 text-orange-700";
  } else if (isInvalid) {
    cellClass += " bg-yellow-50 border-yellow-300 text-yellow-700";
  } else if (children) {
    cellClass += " bg-[#1e40af] border-[#1e40af] text-white shadow-base";
  } else {
    cellClass += " bg-white border-gray-200 text-gray-400 hover:border-blue-300 hover:bg-blue-50/30";
  }

  if (isOver) {
    cellClass += " ring-2 ring-blue-400 ring-offset-1 bg-blue-100 border-blue-400";
  }

  return (
    <td
      ref={setNodeRef}
      className="p-2 border-b border-gray-200 align-middle"
      onContextMenu={handleContextMenu}
      {...props}
    >
      <div className={cellClass}>
        {children || "ドラッグ＆ドロップ"}
      </div>
    </td>
  );
};

const DraggableItem = ({ id, data, children, isDuplicate, isInvalidCapacity, isInvalid }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    data: { value: data },
  });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 1000,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
    >
      {children}
    </div>
  );
};

export default RuralPreferenceTable;



